import React from "react";
import { useTranslation } from "react-i18next";
import { Cart } from "../../types/PricingCalculator";
import { formatCurrency, convert } from "../../utils/pricingLogic";

interface PlanStepProps {
  cart: Cart;
  onUpdate: (updates: Partial<Cart>) => void;
}

const PlanStep: React.FC<PlanStepProps> = ({ cart, onUpdate }) => {
  const { t } = useTranslation();
  // Skip this step if user is eligible for free software
  if (cart.eligibility.isFree) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-300">
            {t("pricingCalc.planStep.title")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t("pricingCalc.eligibilityStep.congratsMsg")}
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-8 w-8 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-green-800">
                {t("pricingCalc.summary.rdbFree")}
              </h3>
              <p className="text-green-700 mt-1">
                {t("pricingCalc.eligibilityStep.congratsMsg")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const planOptions = [
    {
      months: 1 as const,
      discount: 0,
      label: `1 ${t("pricingCalc.planStep.monthly")}`,
      description: t("pricingCalc.planStep.noDiscount"),
    },
    {
      months: 6 as const,
      discount: 10,
      label: `6 ${t("pricingCalc.planStep.monthly")}`,
      description: "10% discount",
    },
    {
      months: 12 as const,
      discount: 20,
      label: `12 ${t("pricingCalc.planStep.monthly")}`,
      description: "20% discount",
    },
    {
      months: 24 as const,
      discount: 35,
      label: `24 ${t("pricingCalc.planStep.monthly")}`,
      description: "35% discount",
    },
  ];

  const handlePlanSelect = (months: 1 | 6 | 12 | 24) => {
    onUpdate({
      termMonths: months,
    });
  };

  const calculateMonthlyPrice = (months: 1 | 6 | 12 | 24) => {
    const basePriceRwf = cart.selections.software
      ? convert(cart.selections.software.price, cart.fx, "RWF")
      : 25 * (cart.fx.RWF / cart.fx.USD);
    const discount =
      planOptions.find((plan) => plan.months === months)?.discount || 0;
    const monthlyPrice = basePriceRwf * (1 - discount / 100);
    const totalPrice = monthlyPrice * months;

    return {
      monthly: monthlyPrice,
      total: totalPrice,
    };
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-300">
          {t("pricingCalc.planStep.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">{t("pricingCalc.planStep.subtitle")}</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              {t("pricingCalc.planStep.features")}
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                {t("pricingCalc.planStep.basePrice")}{" "}
                {formatCurrency(
                  cart.selections.software
                    ? convert(cart.selections.software.price, cart.fx, "RWF")
                    : 25 * (cart.fx.RWF / cart.fx.USD),
                  "RWF"
                )}{" "}
                {t("pricingCalc.planStep.perMonth")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {planOptions.map((plan) => {
          const pricing = calculateMonthlyPrice(plan.months);
          const isSelected = cart.termMonths === plan.months;

          return (
            <button
              key={plan.months}
              onClick={() => handlePlanSelect(plan.months)}
              className={`p-6 border-2 rounded-lg text-left transition-colors ${
                isSelected
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              <div className="flex items-center mb-2">
                <input
                  type="radio"
                  checked={isSelected}
                  onChange={() => handlePlanSelect(plan.months)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-3 text-lg font-medium">{plan.label}</span>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-gray-600">{plan.description}</p>

                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {t("pricingCalc.planStep.monthly")}:
                    </span>
                    <span className="font-medium">
                      {formatCurrency(pricing.monthly, "RWF")}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total:</span>
                    <span className="font-semibold text-lg">
                      {formatCurrency(pricing.total, "RWF")}
                    </span>
                  </div>
                </div>

                {plan.discount > 0 && (
                  <div className="mt-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Save {plan.discount}%
                    </span>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {cart.termMonths && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            {t("pricingCalc.summary.pricingSummary")}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-700">Plan Duration:</span>
              <span className="font-medium">
                {planOptions.find((p) => p.months === cart.termMonths)?.label}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">Discount:</span>
              <span className="font-medium">
                {
                  planOptions.find((p) => p.months === cart.termMonths)
                    ?.discount
                }
                %
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">
                {t("pricingCalc.summary.softwareTotal")}
              </span>
              <span className="font-semibold text-lg">
                {formatCurrency(
                  calculateMonthlyPrice(cart.termMonths).total,
                  "RWF"
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanStep;
